import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchMyProfile, updateMyProfile } from '@/api/me';
import { parseValidationErrors } from '@/api/errors';
import { useMenuHomePath } from '@/hooks/useMenuHomePath';
import { isValidEmail } from '@/utils/email';
import { formatPhoneNumber, isPhoneComplete, PHONE_FORMAT_HINT } from '@/utils/phone';
function formatDate(d) {
    const [y, m, day] = d.split('-');
    if (!y || !m || !day)
        return d;
    return `${day}.${m}.${y}`;
}
export function UserProfilePage() {
    const homeTo = useMenuHomePath();
    const [profile, setProfile] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [nameDraft, setNameDraft] = useState('');
    const [emailDraft, setEmailDraft] = useState('');
    const [phoneDraft, setPhoneDraft] = useState('');
    const [marketingConsentDraft, setMarketingConsentDraft] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [saving, setSaving] = useState(false);
    const load = useCallback(() => {
        setLoading(true);
        setError('');
        fetchMyProfile()
            .then((p) => {
            setProfile(p);
            setNameDraft(p.name);
            setEmailDraft(p.email);
            setPhoneDraft(p.phoneNumber?.trim() ? formatPhoneNumber(p.phoneNumber) : '');
            setMarketingConsentDraft(p.marketingConsent);
        })
            .catch(() => {
            setError('Не удалось загрузить профиль');
        })
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => {
        load();
    }, [load]);
    const startEdit = () => {
        if (!profile)
            return;
        setNameDraft(profile.name);
        setEmailDraft(profile.email);
        setPhoneDraft(profile.phoneNumber?.trim() ? formatPhoneNumber(profile.phoneNumber) : '');
        setMarketingConsentDraft(profile.marketingConsent);
        setEmailError('');
        setPhoneError('');
        setSaveError('');
        setEditing(true);
    };
    const cancelEdit = () => {
        setEditing(false);
        setSaveError('');
        setEmailError('');
        setPhoneError('');
        if (profile) {
            setNameDraft(profile.name);
            setEmailDraft(profile.email);
            setPhoneDraft(profile.phoneNumber?.trim() ? formatPhoneNumber(profile.phoneNumber) : '');
            setMarketingConsentDraft(profile.marketingConsent);
        }
    };
    const handlePhoneChange = (value) => {
        setPhoneDraft(formatPhoneNumber(value));
        setPhoneError('');
    };
    const handleSave = async (e) => {
        e.preventDefault();
        if (!profile)
            return;
        const name = nameDraft.trim();
        if (name.length < 2) {
            setSaveError('Имя слишком короткое');
            return;
        }
        const emailTrim = emailDraft.trim();
        if (!isValidEmail(emailTrim)) {
            setEmailError('Укажите корректный email');
            return;
        }
        const phoneTrim = phoneDraft.trim();
        if (phoneTrim.length > 0 && !isPhoneComplete(phoneTrim)) {
            setPhoneError(`Телефон должен быть в формате ${PHONE_FORMAT_HINT}`);
            return;
        }
        setSaving(true);
        setSaveError('');
        setEmailError('');
        setPhoneError('');
        try {
            const updated = await updateMyProfile({
                name,
                email: emailTrim,
                phoneNumber: phoneTrim.length > 0 ? phoneTrim : '',
                marketingConsent: marketingConsentDraft,
            });
            setProfile(updated);
            setEditing(false);
        }
        catch (err) {
            const ve = parseValidationErrors(err);
            if (ve.email)
                setEmailError(ve.email);
            if (ve.phoneNumber)
                setPhoneError(ve.phoneNumber);
            const res = err && typeof err === 'object' && 'response' in err
                ? err.response
                : null;
            const dataMsg = res?.data && typeof res.data === 'object' && 'message' in res.data
                ? String(res.data.message)
                : '';
            if (res?.status === 409) {
                setSaveError(dataMsg || 'Этот email уже занят');
            }
            else if (res?.status === 400 && (ve.email || ve.phoneNumber)) {
                setSaveError('');
            }
            else {
                setSaveError(dataMsg || 'Не удалось сохранить изменения');
            }
        }
        finally {
            setSaving(false);
        }
    };
    const profileFooterBar = (_jsxs("div", { className: "user-profile__bar", children: [_jsx(Link, { to: homeTo, className: "user-footer-link", children: "\u2190 \u041D\u0430 \u0433\u043B\u0430\u0432\u043D\u0443\u044E" }), !editing && profile ? (_jsx("button", { type: "button", className: "user-btn user-btn--primary", onClick: startEdit, children: "\u0418\u0437\u043C\u0435\u043D\u0438\u0442\u044C \u0434\u0430\u043D\u043D\u044B\u0435" })) : null] }));
    if (loading) {
        return (_jsxs(_Fragment, { children: [_jsx("h1", { className: "user-panel__title", children: "\u041F\u0440\u043E\u0444\u0438\u043B\u044C" }), _jsx("p", { className: "user-muted", children: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430\u2026" }), profileFooterBar] }));
    }
    if (error || !profile) {
        return (_jsxs(_Fragment, { children: [_jsx("h1", { className: "user-panel__title", children: "\u041F\u0440\u043E\u0444\u0438\u043B\u044C" }), _jsx("p", { className: "user-muted", children: error || 'Нет данных' }), profileFooterBar] }));
    }
    return (_jsxs(_Fragment, { children: [_jsx("h1", { className: "user-panel__title", children: "\u041F\u0440\u043E\u0444\u0438\u043B\u044C" }), _jsx("p", { className: "user-muted", children: "\u041B\u0438\u0447\u043D\u044B\u0435 \u0434\u0430\u043D\u043D\u044B\u0435 \u0443\u0447\u0451\u0442\u043D\u043E\u0439 \u0437\u0430\u043F\u0438\u0441\u0438" }), editing ? (_jsxs("form", { className: "user-form", onSubmit: handleSave, style: { maxWidth: '28rem' }, children: [saveError ? (_jsx("p", { className: "user-muted", style: { color: '#991b1b' }, children: saveError })) : null, _jsx("label", { htmlFor: "profile-name", children: "\u0418\u043C\u044F" }), _jsx("input", { id: "profile-name", value: nameDraft, onChange: (e) => setNameDraft(e.target.value), required: true, minLength: 2, maxLength: 255 }), _jsx("label", { htmlFor: "profile-email", children: "Email" }), _jsx("input", { id: "profile-email", type: "email", className: emailError ? 'user-form-input--invalid' : undefined, value: emailDraft, onChange: (e) => {
                            setEmailDraft(e.target.value);
                            setEmailError('');
                        }, required: true, autoComplete: "email" }), emailError ? _jsx("span", { className: "user-form-msg user-form-msg--error", children: emailError }) : null, _jsx("label", { htmlFor: "profile-phone", children: "\u0422\u0435\u043B\u0435\u0444\u043E\u043D" }), _jsx("input", { id: "profile-phone", type: "tel", className: phoneError ? 'user-form-input--invalid' : undefined, value: phoneDraft, onChange: (e) => handlePhoneChange(e.target.value), placeholder: PHONE_FORMAT_HINT, autoComplete: "tel" }), phoneError ? _jsx("span", { className: "user-form-msg user-form-msg--error", children: phoneError }) : null, _jsx("p", { className: "user-muted", style: { fontSize: '0.875rem' }, children: "\u041E\u0441\u0442\u0430\u0432\u044C\u0442\u0435 \u0442\u0435\u043B\u0435\u0444\u043E\u043D \u043F\u0443\u0441\u0442\u044B\u043C, \u0447\u0442\u043E\u0431\u044B \u0443\u0434\u0430\u043B\u0438\u0442\u044C \u043D\u043E\u043C\u0435\u0440 \u0438\u0437 \u043F\u0440\u043E\u0444\u0438\u043B\u044F." }), _jsxs("label", { htmlFor: "profile-marketing-consent", style: { display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }, children: [_jsx("input", { id: "profile-marketing-consent", type: "checkbox", checked: marketingConsentDraft, onChange: (e) => setMarketingConsentDraft(e.target.checked), style: { width: 'auto' } }), _jsx("span", { children: "\u041F\u043E\u043B\u0443\u0447\u0430\u0442\u044C \u0440\u0430\u0441\u0441\u044B\u043B\u043A\u0443 \u043E\u0431 \u0430\u043A\u0446\u0438\u044F\u0445 \u0438 \u043F\u0440\u043E\u043C\u043E\u043A\u043E\u0434\u0430\u0445" })] }), _jsxs("div", { className: "user-address-actions", children: [_jsx("button", { type: "submit", className: "user-btn user-btn--primary", disabled: saving, children: saving ? 'Сохранение…' : 'Сохранить' }), _jsx("button", { type: "button", className: "user-btn", onClick: cancelEdit, disabled: saving, children: "\u041E\u0442\u043C\u0435\u043D\u0430" })] })] })) : (_jsx(_Fragment, { children: _jsxs("dl", { className: "user-dl", children: [_jsx("dt", { children: "\u0418\u043C\u044F" }), _jsx("dd", { children: profile.name }), _jsx("dt", { children: "Email" }), _jsx("dd", { children: profile.email }), _jsx("dt", { children: "\u0414\u0430\u0442\u0430 \u0440\u043E\u0436\u0434\u0435\u043D\u0438\u044F" }), _jsx("dd", { children: formatDate(profile.birthday) }), _jsx("dt", { children: "\u0422\u0435\u043B\u0435\u0444\u043E\u043D" }), _jsx("dd", { children: profile.phoneNumber?.trim() ? formatPhoneNumber(profile.phoneNumber) : '-' }), _jsx("dt", { children: "\u0411\u043E\u043D\u0443\u0441\u044B" }), _jsxs("dd", { children: [Math.floor(profile.bonusBalance), " \u0431\u0430\u043B\u043B\u043E\u0432"] }), _jsx("dt", { children: "\u0420\u0430\u0441\u0441\u044B\u043B\u043A\u0430" }), _jsx("dd", { children: profile.marketingConsent ? 'Подключена' : 'Отключена' })] }) })), profileFooterBar] }));
}
